import React from "react";
import { connect } from "react-redux";
import { API } from "aws-amplify";
import { toast } from "react-toastify";
import LocalTimeStamp from "../../common/LocalTimeStamp";
import SurveyResults from "../survey/SurveyResults";
import { getSurveyOriginationMethodValue } from "../../common/SurveyUtils";
import { Button } from "../../components/Buttons";
import { AssetActivityConsortiumIndicator, AssetButtonFilter, AssetButtonFilterValueCellClear } from "../../icons/Assets";
import _ from "lodash";
import { useQuery } from "../../utils/utils";
import ConfirmModal from "../../common/ConfirmModal";
import { CircularProgress } from "@mui/material";

const FILTERS = [
    { key: "RESPONDED",         title: "Responded" },
    { key: "HAS_TASK",          title: "Has Task" },
    { key: "MARKED_FOR_REVIEW", title: "Marked for Review" },
];

// Keyboard contract (mirrors TenantTypeFilter in SupportTools):
//   Trigger  Enter/Space → open dropdown, focus first item
//   Menu     ArrowDown/Up → move focus between items (wraps)
//   Menu     Enter/Space  → toggle item, keep dropdown open
//   Menu     Escape/Tab   → close; Tab moves to first chip or search
//   Chip ✕   Enter/Space  → remove chip; focus next chip or reopen menu
//   Chip ✕   Tab          → next chip, or fall through to search
function ActivitySurveyFilter({ filters, onToggle, searchRef }) {
    const [open, setOpen] = React.useState(false);
    const [announcement, setAnnouncement] = React.useState('');
    const triggerRef = React.useRef();
    const menuRef = React.useRef();
    const chipRefs = React.useRef([]);
    chipRefs.current = chipRefs.current.slice(0, filters.length);

    const announce = (msg) => {
        // Clear first so the same message re-triggers screen reader if repeated
        setAnnouncement('');
        requestAnimationFrame(() => setAnnouncement(msg));
    };

    const openMenu = () => { setOpen(true); };
    const closeMenu = (returnFocus = true) => {
        setOpen(false);
        if (returnFocus && triggerRef.current) triggerRef.current.focus();
    };

    React.useEffect(() => {
        if (open && menuRef.current) {
            const items = menuRef.current.querySelectorAll('[role="option"]');
            if (items[0]) items[0].focus();
        }
    }, [open]);

    React.useEffect(() => {
        if (!open) return;
        const handleOutsideClick = (e) => {
            if (
                triggerRef.current && !triggerRef.current.contains(e.target) &&
                menuRef.current && !menuRef.current.contains(e.target)
            ) setOpen(false);
        };
        document.addEventListener('mousedown', handleOutsideClick);
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, [open]);

    const handleTriggerKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            open ? closeMenu(false) : openMenu();
        } else if (e.key === 'Tab' && !e.shiftKey) {
            if (chipRefs.current.length > 0 && chipRefs.current[0]) {
                e.preventDefault();
                chipRefs.current[0].focus();
            }
            // no chips → let Tab fall through naturally to search
        }
    };

    const handleMenuKeyDown = (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            if (!menuRef.current) return;
            const items = Array.from(menuRef.current.querySelectorAll('[role="option"]'));
            const idx = items.indexOf(document.activeElement);
            const next = items[(idx + 1) % items.length];
            if (next) next.focus();
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            if (!menuRef.current) return;
            const items = Array.from(menuRef.current.querySelectorAll('[role="option"]'));
            const idx = items.indexOf(document.activeElement);
            const prev = items[(idx - 1 + items.length) % items.length];
            if (prev) prev.focus();
        } else if (e.key === 'Escape') {
            e.preventDefault();
            setOpen(false);
            requestAnimationFrame(() => { if (triggerRef.current) triggerRef.current.focus(); });
        } else if (e.key === 'Tab') {
            e.preventDefault();
            setOpen(false);
            if (chipRefs.current.length > 0 && chipRefs.current[0]) {
                chipRefs.current[0].focus();
            } else if (searchRef && searchRef.current) {
                searchRef.current.focus();
            }
        }
    };

    const handleItemKeyDown = (e, key) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            const label = FILTERS.find(f => f.key === key)?.title;
            const wasSelected = _.includes(filters, key);
            announce(`${label} filter ${wasSelected ? 'removed' : 'added'}`);
            onToggle(key);
        }
    };

    const handleChipKeyDown = (e, key, chipIdx) => {
        e.stopPropagation();
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            const label = FILTERS.find(f => f.key === key)?.title;
            announce(`${label} filter removed`);
            onToggle(key);
            requestAnimationFrame(() => requestAnimationFrame(() => {
                const len = chipRefs.current.length;
                if (len > 0) {
                    const next = chipRefs.current[Math.min(chipIdx, len - 1)];
                    if (next) next.focus();
                } else {
                    openMenu();
                }
            }));
        } else if (e.key === 'Tab' && !e.shiftKey) {
            const nextChip = chipRefs.current[chipIdx + 1];
            if (nextChip) { e.preventDefault(); nextChip.focus(); }
            // last chip → fall through to search naturally
        } else if (e.key === 'Tab' && e.shiftKey) {
            e.preventDefault();
            const prevChip = chipRefs.current[chipIdx - 1];
            if (prevChip) prevChip.focus();
            else triggerRef.current.focus();
        }
    };

    const activeLabel = filters.length ? `Survey filter, ${filters.length} active` : 'Survey filter';
    const activeFilters = filters.map(key => FILTERS.find(f => f.key === key)).filter(Boolean);

    return (
        <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'flex-start', height: '32px' }}>
            <div
                aria-live="polite"
                aria-atomic="true"
                style={{ position: 'absolute', width: '1px', height: '1px', overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap' }}
            >
                {announcement}
            </div>
            <div
                ref={triggerRef}
                tabIndex={0}
                role="button"
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-label={activeLabel}
                className={`filterButton navAction${filters.length ? ' isFiltered' : ''}`}
                onClick={() => open ? closeMenu(false) : openMenu()}
                onKeyDown={handleTriggerKeyDown}
            >
                <AssetButtonFilter />
                <div className="filterButtonTitle">Filter</div>
                {activeFilters.map((f, i) => (
                    <div className="filterButtonValue" key={f.key}>
                        <div className="filterButtonValueCell" onClick={e => { e.stopPropagation(); announce(`${f.title} filter removed`); onToggle(f.key); }}>
                            <div className="filterButtonValueCellTitle">{f.title}</div>
                            <div
                                role="button"
                                aria-label={`Remove ${f.title} filter`}
                                tabIndex={0}
                                className="filterButtonValueCellClear"
                                ref={el => { chipRefs.current[i] = el; }}
                                onClick={e => { e.stopPropagation(); announce(`${f.title} filter removed`); onToggle(f.key); }}
                                onKeyDown={e => handleChipKeyDown(e, f.key, i)}
                            >
                                <AssetButtonFilterValueCellClear />
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {open && (
                <div
                    ref={menuRef}
                    className="pullDownMenu"
                    role="listbox"
                    aria-label="Survey filter options"
                    onKeyDown={handleMenuKeyDown}
                    style={{ position: 'absolute', top: '40px', left: 0, zIndex: 200, minWidth: '180px', height: 'auto' }}
                >
                    {FILTERS.map(f => (
                        <div
                            key={f.key}
                            role="option"
                            tabIndex={-1}
                            aria-selected={_.includes(filters, f.key)}
                            className={`menuCell ${_.includes(filters, f.key) ? 'selected' : ''}`}
                            onClick={() => { const wasSelected = _.includes(filters, f.key); announce(`${f.title} filter ${wasSelected ? 'removed' : 'added'}`); onToggle(f.key); setOpen(false); }}
                            onKeyDown={e => handleItemKeyDown(e, f.key)}
                        >
                            <div className="menuItemTitle">{f.title}</div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

const mapStateToProps = state => ({
    surveyStatus: state.surveyStatus,
    user: state.user,
})

function SurveyStatusDetail(props) {
    const { surveyStatus } = props
    switch (surveyStatus) {
        case 'COMPLETE':
            return (
                <div>Answered</div>
            )
        case 'SENT':
            return (
                <div>Sent</div>
            )
        case 'CONTACT_DUPLICATE':
            return (
                <div>Contact Duplicate</div>
            )
        case 'CREATED':
            return (
                <div>Created</div>
            )
        case 'MISMATCH_INBOUND_MESSAGE':
            return (
                <div>Mismatched Message</div>
            )
        default:
            return (
                <div>{props.surveyStatus}</div>
            )
    }
}

function SurveyStatusButton(props) {
    const { surveyStatus } = props
    switch (surveyStatus) {
        case 'COMPLETE':
            return (
                <Button
                    style="primaryProminent"
                    size="small"
                    title="View Answers"
                />
            )
        case 'SENT':
            return (
                <Button
                    style="secondary"
                    size="small"
                    title="Sent"
                />
            )
        case 'CONTACT_DUPLICATE':
            return (
                <Button
                    style="disabled"
                    size="small"
                    title="Contact Duplicate"
                />
            )
        case 'CREATED':
            return (
                <Button
                    style="secondary"
                    size="small"
                    title="Created"
                />
            )
        case 'MISMATCH_INBOUND_MESSAGE':
            return (
                <Button
                    style="secondary"
                    size="small"
                    title="Mismatch Inbound Message"
                />
            )
        default:
            return (
                <Button
                    style="disabled"
                    size="small"
                    title={props.surveyStatus}
                />
            )
    }
}

function ActivitySurvey(props) {
    const [surveyInstances, setSurveyInstances] = React.useState([]);
    const [activePage, setActivePage] = React.useState({});
    const [page, setPage] = React.useState(0);
    const [searchFocused, setSearchFocused] = React.useState(false);
    const [searchVal, setSearchVal] = React.useState("");
    const [searchInput, setSearchInput] = React.useState("");
    const [searchAnnouncement, setSearchAnnouncement] = React.useState("");
    const [filters, setFilters] = React.useState([]);
    const searchRef = React.useRef();
    const [autoOpenModal, setAutoOpenModal] = React.useState(useQuery().get("openSurveyResults"));
    const [loading, setLoading] = React.useState(false);

    const surveyInstanceIdParam = useQuery().get("surveyInstanceId");

    React.useEffect(() => {
        loadSurveys()
    }, []);

    const loadFirstPage = (searchParam) => {
        setPage(0);
        loadSurveys(true, 0, searchParam);
    }

    const toggleFilter = (filter) => {
        if (_.includes(filters, filter)) {
            _.remove(filters, (remFilter) => remFilter === filter);
            setFilters(Object.assign([], filters));
        } else {
            filters.push(filter);
            setFilters(Object.assign([], filters));
        }
        loadFirstPage(searchRef.current.value);
    }

    const loadSurveys = (isSearch = false, pageNum = 0, searchParam = "", filtersParam = filters) => {
        let init = {
            queryStringParameters: {
                page: pageNum,
                search: searchParam,
                size: 30
            }
        }

        if (surveyInstanceIdParam)
            init.queryStringParameters.surveyInstanceId = surveyInstanceIdParam;

        if (filtersParam) {
            _.forEach(filtersParam, (f) => {
                // eslint-disable-next-line default-case
                switch (f) {
                    case "RESPONDED":
                        init.queryStringParameters.statuses = ["STARTED", "COMPLETE"];
                        break;
                    case "HAS_TASK":
                        init.queryStringParameters.hasTask = true;
                        break;
                    case "MARKED_FOR_REVIEW":
                        init.queryStringParameters.review = true;
                        break;
                }
            })
        }

        return API.get('Core', '/api/v1/survey/instance', init)
            .then(response => {
                // if Searching then just set the content
                if (isSearch) setSurveyInstances(response.content);
                else setSurveyInstances(surveyInstances.concat(response.content));

                setActivePage(response);
            },
                error => {
                    toast("Could not load survey results: " + error.response.data.message, { position: "top-center", type: "error" });
                }
            );
    }

    const onSurveyInstanceChange = (s) => {
        let instance = _.findIndex(surveyInstances, function (si) {
            return si?.surveyInstance?.id === s?.surveyInstance?.id;
        })
        surveyInstances[instance] = s;
        setSurveyInstances([].concat(surveyInstances));
    }

    const loadMore = () => {
        let nextPage = page + 1;
        setPage(nextPage);
        loadSurveys(false, nextPage, searchRef.current.value);
    }

    const keyPress = (e) => {
        if (e.keyCode === 13) {
            setSearchVal(searchRef.current.value);
            loadFirstPage(searchRef.current.value);
        }
    }

    const clearSearch = () => {
        setSearchInput("");
        setSearchVal("");
        setSearchAnnouncement("");
        requestAnimationFrame(() => setSearchAnnouncement("Search cleared"));
        loadFirstPage("");
    }

    const onSearchFocused = () => {
        setSearchFocused(true);
    }

    const onSearchBlurred = () => {
        setSearchFocused(false);
    }

    const onSearchChange = (e) => {
        setSearchInput(searchRef.current.value);
    }

    const canAutoOpenModal = () => {
        return surveyInstances && surveyInstances.length === 1 && autoOpenModal === '1'
    }

    const focusSurveyTab = () => {
        const bar = document.querySelector('[role="tablist"][aria-label="Activity tabs"]');
        if (bar) { const active = bar.querySelector('[aria-selected="true"]'); if (active) active.focus(); }
    };

    const showDeleteButton = () => props?.user?.aboutMe?.user?.userType === "INTERNAL_SUPPORT";

    const deleteSurveyInstance = survey => {
        const INSTANCE_ID = survey?.surveyInstance?.id;

        if(INSTANCE_ID != null) {
            API.del('Core', `/api/v1/survey/instance/${INSTANCE_ID}`)
            .then(() => {
                setLoading(true);
                return loadSurveys(true);
            })
            .then(() => setLoading(false));   
        }
    }

    return (
        <div>
            <div className="secondaryNavBar">
                <div className="navTitle">
                    <div className="viewName">Surveys</div>
                    <div className="counterBadge">
                        <div className="counterValue">{activePage.totalElements}</div>
                    </div>
                </div>

                <div className="navActions">
                    <ActivitySurveyFilter filters={filters} onToggle={toggleFilter} searchRef={searchRef} />

                    <div className={`searchInput ${searchFocused ? "isFocused" : ""} ${(searchInput.length > 0) ? "hasInput" : ""}`}>
                        <div className="icon" aria-hidden="true" />
                        <div
                            aria-live="polite"
                            aria-atomic="true"
                            style={{ position: 'absolute', width: '1px', height: '1px', overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap' }}
                        >
                            {searchAnnouncement}
                        </div>
                        <input value={searchInput} onChange={onSearchChange} placeholder={"Search Surveys"} aria-label="Search Surveys" onFocus={onSearchFocused} onBlur={onSearchBlurred} className="input" onKeyDown={keyPress} type="text" ref={searchRef} />
                        <div
                            className="clear"
                            onClick={clearSearch}
                            role="button"
                            tabIndex={searchInput.length > 0 ? 0 : -1}
                            aria-hidden={searchInput.length === 0}
                            aria-label="Clear search"
                            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); clearSearch(); } }}
                        />
                    </div>
                </div>
            </div>

            {!loading ? <div className="activitySection sectionSurveys">
                {
                    _.map(surveyInstances, (s) => {
                        return (
                            <SurveyResults key={s.surveyInstance.id} surveyInstanceId={s.surveyInstance.id} onChange={onSurveyInstanceChange.bind(this)} opened={canAutoOpenModal()} setAutoOpenModal={setAutoOpenModal}>
                                <div className="tableCell activitySurveyCell">

                                    <div className="cellDetails">
                                        {
                                            s.surveyInstance.review ? (
                                                <div className="surveyCellDetail">
                                                    <div className="reviewIndicator">
                                                        <div className="indicatorTitle">REVIEW</div>
                                                    </div>
                                                </div>
                                            ) : null
                                        }
                                        {s.surveyInstance.tenantId !== props.user.aboutMe.currentTenant.id ? (
                                            <div className="bodyColumn columnConsortium">
                                                <div className="consortiumIndicator">
                                                    <AssetActivityConsortiumIndicator className="indicatorIcon" />
                                                </div>
                                            </div>
                                        ) : null}
                                        <div className="surveyCellDetail detailPrimary">
                                            <div className="detailTitle">{s.surveyInstance.surveyName}</div>
                                            <div className="detailValue"><LocalTimeStamp utcTimestamp={s.surveyInstance.createdAt} format={"MM/DD/YYYY, h:mm:ss a"} /></div>
                                        </div>

                                        <div className="surveyCellDetail detailSecondary">
                                            <div className="detailTitle">{s.surveyInstance.contact.readableName}</div>
                                            <div className="detailValue"><SurveyStatusDetail surveyStatus={s.surveyInstance.status} /></div>
                                        </div>

                                        <div className="surveyCellDetail detailTertiary">
                                            <div className="detailTitle">{getSurveyOriginationMethodValue(s)}</div>
                                            <div className="detailValue">Origin</div>
                                        </div>
                                        {
                                            s.tasks && s.tasks[0] ? (
                                                <div className="surveyCellDetail">
                                                    <div className="detailTitle">{s.tasks[0].taskTypeName} ({s.tasks[0]['status']})</div>
                                                    <div className="detailValue">Task</div>
                                                </div>
                                            ) : null
                                        }
                                    </div>


                                    <div className="cellActions" onClick={e => e.stopPropagation()}>
                                        {
                                            showDeleteButton() &&
                                            <ConfirmModal
                                            title="Delete"
                                            text={
                                                <>
                                                    <div>Are you sure you want to delete this survey? All feedback received from this survey will also be deleted.</div>
                                                    <div style={{ marginTop: '10px' }}>If you update the Rule configuration instead, this survey will no longer be sent and its responses will remain.</div>
                                                </>
                                            }
                                            onConfirm={() => deleteSurveyInstance(s)}
                                            onCancel={() => {}}
                                            confirmTitle={"Delete Survey"}>      
                                                <div className="button destructive small">
                                                            <div className="title">Delete</div>
                                                        </div>
                                            </ConfirmModal>
                                        }
                                        <SurveyStatusButton surveyStatus={s.surveyInstance.status} />
                                    </div>
                                </div>
                            </SurveyResults>
                        )
                    })
                }
                {
                    (activePage && !activePage.last) ?
                        (
                            <div
                                onClick={loadMore}
                                className="encountersExpander"
                                role="button"
                                tabIndex={0}
                                aria-label="Show more surveys"
                                onKeyDown={e => {
                                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); loadMore(); }
                                    else if (e.key === 'Tab' && !e.shiftKey) { e.preventDefault(); focusSurveyTab(); }
                                }}
                            >
                                <div className="expanderButton">
                                    <div className="title">Show More</div>
                                    <div className="icon" aria-hidden="true" />
                                </div>
                            </div>
                        ) : null
                }
                {surveyInstances.length > 0 && (
                    <div
                        tabIndex={0}
                        style={{ height: 0, overflow: 'hidden', outline: 'none' }}
                        onFocus={focusSurveyTab}
                    />
                )}
            </div> : <CircularProgress />}
        </div>
    );
}

export default connect(mapStateToProps)(ActivitySurvey);
